import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Link2, Loader2, Search, Trash2, Upload, X } from 'lucide-react';
import {
  TIPOS_ENTIDAD,
  guardarLogoUrl,
  listarEntidades,
  quitarLogo,
  subirLogo,
  tipoDeEntidad
} from '../services/entidadLogos';
import LogoEntidad from './LogoEntidad';
import './admin-entidades.scss';

// Pestaña «Entidades» del panel: el logo de cada institución para el listado de
// «Trabaja para el Estado».
//
// Las entidades vienen **ordenadas por número de convocatorias**, no por
// nombre, y eso es el punto: hay 488 entidades distintas y el reparto está muy
// desigual (el Poder Judicial solo son 453 filas, los 21 ministerios otras
// 535). Subiendo de arriba hacia abajo se cubre la mitad del listado con unos
// 50 logos, y se puede parar en cualquier momento: lo que no tenga logo propio
// cae al icono por tipo de entidad.
const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'sin', label: 'Sin logo propio' },
  { id: 'con', label: 'Con logo propio' }
];

const AdminEntidades = () => {
  const [entidades, setEntidades] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [ocupada, setOcupada] = useState(null);   // clave en proceso
  const [editandoUrl, setEditandoUrl] = useState(null);
  const [urlNueva, setUrlNueva] = useState('');
  const [aviso, setAviso] = useState('');

  // Un input de archivo para toda la tabla: 488 inputs ocultos no hacen falta.
  const inputArchivo = useRef(null);
  const entidadDelArchivo = useRef(null);

  const cargar = async () => {
    setCargando(true);
    setError('');
    try {
      setEntidades(await listarEntidades());
    } catch (e) {
      console.error('No se pudieron leer las entidades:', e);
      setError(
        e?.code === '42P01'
          ? 'Falta la vista «entidades_convocatorias» en Supabase (migración 202610310002).'
          : `No se pudieron leer las entidades: ${e?.message || 'error desconocido'}`
      );
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const resumen = useMemo(() => {
    const total = entidades.length;
    const conLogo = entidades.filter((e) => e.logo_url);
    const filas = entidades.reduce((s, e) => s + Number(e.convocatorias || 0), 0);
    const filasCubiertas = conLogo.reduce((s, e) => s + Number(e.convocatorias || 0), 0);
    // Lo que el icono por tipo resuelve sin subir nada.
    const conTipo = entidades.filter((e) => !e.logo_url && tipoDeEntidad(e.nombre) !== 'otra');
    const filasTipo = conTipo.reduce((s, e) => s + Number(e.convocatorias || 0), 0);
    return {
      total,
      conLogo: conLogo.length,
      filas,
      pctLogo: filas ? Math.round((filasCubiertas / filas) * 100) : 0,
      pctTipo: filas ? Math.round((filasTipo / filas) * 100) : 0
    };
  }, [entidades]);

  const visibles = useMemo(() => {
    const texto = busqueda
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();

    return entidades.filter((e) => {
      if (filtro === 'sin' && e.logo_url) return false;
      if (filtro === 'con' && !e.logo_url) return false;
      if (!texto) return true;
      return String(e.nombre || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .includes(texto);
    });
  }, [entidades, busqueda, filtro]);

  // Refresca una sola fila en memoria: volver a leer las 488 tras cada subida
  // sería un viaje de más y haría saltar la tabla.
  const actualizarFila = (clave, cambios) => {
    setEntidades((prev) => prev.map((e) => (e.clave === clave ? { ...e, ...cambios } : e)));
  };

  const pedirArchivo = (entidad) => {
    entidadDelArchivo.current = entidad;
    if (inputArchivo.current) {
      inputArchivo.current.value = '';
      inputArchivo.current.click();
    }
  };

  const alElegirArchivo = async (evento) => {
    const archivo = evento.target.files?.[0];
    const entidad = entidadDelArchivo.current;
    if (!archivo || !entidad) return;

    setOcupada(entidad.clave);
    setAviso('');
    setError('');
    try {
      const url = await subirLogo({ clave: entidad.clave, nombre: entidad.nombre, archivo });
      actualizarFila(entidad.clave, { logo_url: url, logo_origen: 'archivo' });
      setAviso(`Logo de ${entidad.nombre} actualizado.`);
    } catch (e) {
      console.error('No se pudo subir el logo:', e);
      setError(`No se pudo subir el logo: ${e?.message || 'error desconocido'}`);
    } finally {
      setOcupada(null);
      entidadDelArchivo.current = null;
    }
  };

  const guardarUrl = async (entidad) => {
    setOcupada(entidad.clave);
    setAviso('');
    setError('');
    try {
      const url = await guardarLogoUrl({
        clave: entidad.clave,
        nombre: entidad.nombre,
        url: urlNueva
      });
      actualizarFila(entidad.clave, { logo_url: url, logo_origen: 'url' });
      setEditandoUrl(null);
      setUrlNueva('');
      setAviso(`Logo de ${entidad.nombre} actualizado.`);
    } catch (e) {
      setError(e?.message || 'No se pudo guardar la dirección');
    } finally {
      setOcupada(null);
    }
  };

  const quitar = async (entidad) => {
    setOcupada(entidad.clave);
    setAviso('');
    setError('');
    try {
      await quitarLogo(entidad.clave);
      actualizarFila(entidad.clave, { logo_url: null, logo_origen: null });
      setAviso(`Se quitó el logo de ${entidad.nombre}.`);
    } catch (e) {
      console.error('No se pudo quitar el logo:', e);
      setError(`No se pudo quitar el logo: ${e?.message || 'error desconocido'}`);
    } finally {
      setOcupada(null);
    }
  };

  return (
    <div className="ae">
      <input
        ref={inputArchivo}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={alElegirArchivo}
        hidden
      />

      <div className="ae-head">
        <div>
          <h4 className="ae-head__titulo">Logos de las entidades</h4>
          <p className="ae-head__sub">
            Aparecen en cada fila de «Trabaja para el Estado». Están ordenadas por número de
            convocatorias: subiendo de arriba hacia abajo cubres mucho con pocos logos. Lo que
            no tenga logo propio muestra el icono de su tipo de entidad.
          </p>
        </div>
        <button type="button" className="ae-btn" onClick={cargar} disabled={cargando}>
          Recargar
        </button>
      </div>

      <div className="ae-kpis">
        <div className="ae-kpi">
          <span className="ae-kpi__label">Entidades</span>
          <strong className="ae-kpi__valor">{resumen.total.toLocaleString('en-US')}</strong>
          <span className="ae-kpi__hint">{resumen.filas.toLocaleString('en-US')} convocatorias</span>
        </div>
        <div className="ae-kpi">
          <span className="ae-kpi__label">Con logo propio</span>
          <strong className="ae-kpi__valor">{resumen.conLogo.toLocaleString('en-US')}</strong>
          <span className="ae-kpi__hint">cubren el {resumen.pctLogo}% de las filas</span>
        </div>
        <div className="ae-kpi">
          <span className="ae-kpi__label">Resueltas por tipo</span>
          <strong className="ae-kpi__valor">{resumen.pctTipo}%</strong>
          <span className="ae-kpi__hint">de las filas, sin subir nada</span>
        </div>
      </div>

      <div className="ae-barra">
        <div className="ae-buscador">
          <Search size={15} />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar entidad…"
            aria-label="Buscar entidad"
          />
          {busqueda && (
            <button type="button" onClick={() => setBusqueda('')} title="Limpiar">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="ae-filtros">
          {FILTROS.map((f) => (
            <button
              type="button"
              key={f.id}
              className={`ae-filtro ${filtro === f.id ? 'is-activo' : ''}`}
              onClick={() => setFiltro(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <span className="ae-barra__conteo">
          {visibles.length.toLocaleString('en-US')} de {resumen.total.toLocaleString('en-US')}
        </span>
      </div>

      {error && <div className="ae-alerta ae-alerta--error">{error}</div>}
      {aviso && !error && (
        <div className="ae-alerta ae-alerta--ok">
          <Check size={15} />
          {aviso}
        </div>
      )}

      {cargando ? (
        <p className="ae-vacio">Cargando entidades…</p>
      ) : visibles.length === 0 ? (
        <p className="ae-vacio">Ninguna entidad coincide con la búsqueda.</p>
      ) : (
        <div className="ae-tabla">
          {visibles.map((entidad) => {
            const tipo = tipoDeEntidad(entidad.nombre);
            const trabajando = ocupada === entidad.clave;

            return (
              <div className="ae-fila" key={entidad.clave}>
                <LogoEntidad nombre={entidad.nombre} url={entidad.logo_url} tamano={44} />

                <div className="ae-fila__texto">
                  <div className="ae-fila__nombre">{entidad.nombre}</div>
                  <div className="ae-fila__meta">
                    <span className="ae-tag">
                      {Number(entidad.convocatorias).toLocaleString('en-US')} convocatorias
                    </span>
                    {Number(entidad.abiertas) > 0 && (
                      <span className="ae-tag ae-tag--abiertas">
                        {Number(entidad.abiertas).toLocaleString('en-US')} abiertas
                      </span>
                    )}
                    <span className="ae-tag ae-tag--tipo">{TIPOS_ENTIDAD[tipo]?.label}</span>
                    {entidad.logo_url && (
                      <span className="ae-tag ae-tag--logo">
                        {entidad.logo_origen === 'url' ? 'Logo por enlace' : 'Logo subido'}
                      </span>
                    )}
                  </div>

                  {editandoUrl === entidad.clave && (
                    <div className="ae-url">
                      <input
                        type="url"
                        value={urlNueva}
                        onChange={(e) => setUrlNueva(e.target.value)}
                        placeholder="https://www.gob.pe/…/logo.png"
                        aria-label={`Dirección del logo de ${entidad.nombre}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') guardarUrl(entidad); }}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="ae-btn ae-btn--chico ae-btn--principal"
                        onClick={() => guardarUrl(entidad)}
                        disabled={!urlNueva.trim() || trabajando}
                      >
                        Guardar
                      </button>
                      <button
                        type="button"
                        className="ae-btn ae-btn--chico"
                        onClick={() => { setEditandoUrl(null); setUrlNueva(''); }}
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>

                <div className="ae-fila__acciones">
                  {trabajando ? (
                    <span className="ae-trabajando">
                      <Loader2 size={15} className="ae-girar" />
                    </span>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="ae-btn ae-btn--chico"
                        onClick={() => pedirArchivo(entidad)}
                        title="Subir una imagen (se reduce a 128 px antes de guardarla)"
                      >
                        <Upload size={14} />
                        Subir
                      </button>
                      <button
                        type="button"
                        className="ae-btn ae-btn--chico"
                        onClick={() => {
                          setEditandoUrl(entidad.clave);
                          setUrlNueva(entidad.logo_origen === 'url' ? entidad.logo_url || '' : '');
                        }}
                        title="Pegar la dirección de una imagen"
                      >
                        <Link2 size={14} />
                        Enlace
                      </button>
                      {entidad.logo_url && (
                        <button
                          type="button"
                          className="ae-btn ae-btn--chico ae-btn--quitar"
                          onClick={() => quitar(entidad)}
                          title="Quitar el logo"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminEntidades;
